import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { EnforcementService } from '../../shared/enforcement/enforcement.service';
import { EnforcementStatus } from '../../shared/enforcement/enforcement.types';

/**
 * Confines a locked user to the /locked page and the specific blocking request
 * detail pages (/pages/visa-requests/<id>). Any other route → redirected to
 * /locked via a UrlTree (atomic redirect).
 *
 * - canActivate: runs on entry into /pages — always fetches fresh status.
 * - canActivateChild: runs on EVERY child navigation — uses the cached status
 *   (no network per click); only falls back to a fetch if nothing is cached yet.
 *
 * Fails OPEN on error/slow status: the server's 423 on any mutation is the real
 * lock, so a transient status problem must not trap the whole UI.
 */
@Injectable()
export class EnforcementGuard implements CanActivate, CanActivateChild {
  constructor(
    private enforcementService: EnforcementService,
    private router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.fetchAndDecide(state.url);
  }

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const cached = this.enforcementService.cachedStatus;
    if (cached) return this.decide(cached, state.url);
    return this.fetchAndDecide(state.url);
  }

  private fetchAndDecide(url: string): Observable<boolean | UrlTree> {
    return this.enforcementService.getStatus().pipe(
      timeout(6000),
      map((status) => this.decide(status, url)),
      catchError(() => of(true))
    );
  }

  private decide(status: EnforcementStatus, url: string): boolean | UrlTree {
    if (!status?.locked) return true;

    // Allow only a blocking request's detail page: /pages/visa-requests/<id>
    const match = url.match(/^\/pages\/visa-requests\/(\d+)(?:[?#]|$)/);
    if (match) {
      const id = Number(match[1]);
      if (this.enforcementService.allowedRequestIds(status).includes(id)) {
        return true;
      }
    }

    return this.router.parseUrl('/locked');
  }
}
